import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut, Loader2, ClipboardList, Users, Trophy, LayoutList, Clock, BarChart3, ListTodo, Store, Image as ImageIcon, Megaphone, GraduationCap, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import MyWorks from "@/components/staff/MyWorks";
import DeptReview from "@/components/staff/DeptReview";
import StaffAdmin from "@/components/staff/StaffAdmin";
import StaffRanking from "@/components/staff/StaffRanking";
import StaffStats from "@/components/staff/StaffStats";
import StaffTasks from "@/components/staff/StaffTasks";
import AdminPartners from "@/components/staff/AdminPartners";
import AdminSite from "@/components/staff/AdminSite";
import AdminSlidesTexts from "@/components/staff/AdminSlidesTexts";
import AdminGallery from "@/components/staff/AdminGallery";
import AdminComms from "@/components/staff/AdminComms";
import AdminFormations from "@/components/staff/AdminFormations";
import StaffTrash from "@/components/staff/StaffTrash";

const ROLE_LABEL = { employe: "Employé", responsable: "Responsable", gerant: "Gérant" };

export default function Espace() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mine");

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f4f7fa]">
        <Loader2 size={36} className="animate-spin text-korban-navy2" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/connexion" replace />;

  if (!user.active) {
    return (
      <div className="grain flex min-h-screen items-center justify-center bg-korban-navy px-5" data-testid="pending-account-view">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.05] p-10 text-center backdrop-blur-xl">
          <img src="/logo_korban_consulting.png" alt="KORBAN" className="mx-auto h-16 w-16 rounded-full bg-white p-1.5" />
          <h1 className="mt-6 font-display text-2xl font-bold text-white">Bienvenue, {user.full_name}</h1>
          <p className="mt-4 leading-relaxed text-slate-300">
            Votre demande d'accès <b className="text-korban-gold">(département : {user.dept})</b> a été envoyée. Le
            gérant doit la valider avant que vous puissiez travailler ici.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            <Link to="/" className="rounded-lg bg-korban-gold px-6 py-3 font-bold text-korban-navy">Retour au site</Link>
            <button
              onClick={() => logout(navigate)}
              data-testid="pending-logout-button"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 px-6 py-3 font-semibold text-white hover:bg-white/10"
            >
              <LogOut size={16} /> Se déconnecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isGerant = user.role === "gerant";
  const isResponsable = user.role === "responsable";
  const tabs = [
    { id: "mine", label: "Mes travaux", icon: ClipboardList },
    { id: "tasks", label: "Tâches", icon: ListTodo },
    ...(isGerant
      ? [
          { id: "all", label: "Tous les travaux", icon: LayoutList },
          { id: "staff", label: "Personnel", icon: Users },
          { id: "stats", label: "Statistiques", icon: BarChart3 },
          { id: "partners", label: "Partenaires", icon: Store },
          { id: "formations", label: "Formations", icon: GraduationCap },
          { id: "site", label: "Site & photos", icon: ImageIcon },
          { id: "comms", label: "Communication", icon: Megaphone },
          { id: "trash", label: "Corbeille", icon: Trash2 },
        ]
      : isResponsable
        ? [{ id: "dept", label: "Département", icon: LayoutList }]
        : []),
    { id: "rank", label: "Classement", icon: Trophy },
  ];
  const activeTab = tabs.some((t) => t.id === tab) ? tab : "mine";

  return (
    <div className="min-h-screen bg-[#f4f7fa]" data-testid="espace-workspace">
      <header className="sticky top-0 z-40 border-b-[3px] border-korban-gold bg-korban-navy">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white p-1">
              <img src="/logo_korban_consulting.png" alt="KORBAN" className="h-full w-full object-contain" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-white sm:text-base" data-testid="user-full-name">{user.full_name}</p>
              <p className="truncate text-xs text-slate-400">
                <span className="font-semibold text-korban-gold">{ROLE_LABEL[user.role]}</span> · {user.dept}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="espace-back-site" className="hidden rounded-md border border-white/20 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 sm:block">
              Site web
            </Link>
            <button
              onClick={() => logout(navigate)}
              data-testid="espace-logout-button"
              className="inline-flex items-center gap-1.5 rounded-md bg-korban-gold px-3.5 py-2 text-xs font-bold text-korban-navy hover:bg-korban-golddark"
            >
              <LogOut size={14} /> Quitter
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-3 pb-2">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`espace-tab-${id}`}
              className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-2 text-[0.82rem] font-bold transition-all ${
                activeTab === id ? "bg-korban-gold text-korban-navy" : "text-slate-300 hover:bg-white/10"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        {activeTab === "mine" && <MyWorks user={user} />}
        {activeTab === "tasks" && <StaffTasks user={user} />}
        {activeTab === "dept" && <DeptReview user={user} scope="dept" />}
        {activeTab === "all" && <DeptReview user={user} scope="all" />}
        {activeTab === "staff" && <StaffAdmin user={user} />}
        {activeTab === "rank" && <StaffRanking user={user} />}
        {activeTab === "stats" && <StaffStats user={user} />}
        {activeTab === "partners" && <AdminPartners user={user} />}
        {activeTab === "formations" && <AdminFormations user={user} />}
        {activeTab === "site" && (
          <>
            <AdminSite user={user} />
            <div className="my-8 h-px bg-slate-200" />
            <AdminSlidesTexts user={user} />
            <div className="my-8 h-px bg-slate-200" />
            <AdminGallery user={user} />
          </>
        )}
        {activeTab === "comms" && <AdminComms user={user} />}
        {activeTab === "trash" && <StaffTrash user={user} />}
      </main>

      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-500">
        KORBAN CONSULTING — Espace personnel · L'historique de travail n'est jamais supprimé
      </footer>
    </div>
  );
}
